import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import HighStock from 'highcharts/highstock';
import type { Options as HighchartsOptions } from 'highcharts';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCNoData from 'highcharts/modules/no-data-to-display';
import { useQuery } from 'react-query';
import { queryOptions } from '~/shared/constants';
import { fetchIndicatorData } from '~/shared/services/api';
import moment from 'moment';
import styled from 'styled-components';
import { AlertBar, Loader, SeriesControls } from '~/shared/components';
import { lineChartHighchartsTooltipCfg } from '~/shared/utils/lineChartTooltip';
import { colors, plotBandOpacity } from '~/shared/constants';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { simpleMovingAverage, resolveColorToken } from '~/shared/utils';
import { resolveColumnNodeDescription, buildColumnDescriptionByName } from '~/shared/utils/columnNodeDescription';
import {
  applyRangeSelectorToChart,
  areChartXExtremesCanonicalForRangeIndex,
  DEFAULT_RANGE_SELECTOR_INDEX,
  extractSeriesXValues,
  MAX_RANGE_SELECTOR_INDEX,
  getRangeSelectorIndexFromWindow,
  readMainAxisMaxForDurationPreset,
} from '~/shared/utils/timeWindow';
import {
  deriveAxisLimits,
  readFiniteXExtentsFromAxis,
  xExtentsDiffer,
  pickXExtentsFromAfterSetExtremesEvent,
  shouldPersistUserXExtentsFromEvent,
  type PointLike,
} from '~/shared/utils/chartAxis';
import { useSmaState, DEFAULT_SMA_VALUE } from './hooks/useSmaState';
import { ControlsWrapper } from './IndicatorLineChart/IndicatorLineChart.styles';
import { IndicatorSmaControls } from '~/shared/components';
import { getLineChartExportMenuItems, registerLineChartExport } from '~/shared/utils/lineChartExport';
import { syncLineChartLegendTooltips } from '~/shared/utils/lineChartLegendTooltip';
import {
  applyLineChartSelectionToChart,
  syncLineChartSelectedSeriesFromChart,
  useLineChartLegendClickHandlers,
  scheduleLineChartSelectedSeriesSync,
} from '~/shared/utils/lineChartSelectionSync';
import {
  appendDoubleYAxisChartSeries,
  buildDoubleYAxisLegendOptions,
  doubleYAxisLegendLabelFormatter,
  isLegendAxisDivider,
  maintainDoubleYAxisInlineLegendDivider,
  resolveInitialDoubleYAxisVisibleNames,
  resolveDoubleYAxisChartLayout,
} from '~/shared/utils/doubleYAxisLegend';

const LegendControlsWrapper = styled(ControlsWrapper)`
  right: 80px;
`;

type ChartSeriesConfig = {
  type: string;
  name?: string;
  data?: PointLike[];
  yAxis?: string;
  color?: string;
  showInNavigator?: boolean;
  showInLegend?: boolean;
  visible?: boolean;
  custom?: {
    description?: string;
  };
};

const PRIMARY_AXIS_ID = 'returns-axis';
const SECONDARY_AXIS_ID = 'sentiment-axis';

const DOUBLE_Y_AXIS_LEGEND_CONFIG = {
  leftAxisId: SECONDARY_AXIS_ID,
  rightAxisId: PRIMARY_AXIS_ID,
  defaultAxisId: PRIMARY_AXIS_ID,
};

HCExporting(HighStock);
HCNoData(HighStock);
HCOfflineExporting(HighStock);
HCAccessibility(HighStock);
registerLineChartExport(HighStock);

const EMPTY_STOCK_OPTIONS: HighchartsOptions = { series: [] };

const normalizeSeriesData = (input?: PointLike[]): Array<[number, number | null]> => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .map(point => {
      if (Array.isArray(point)) {
        const [rawX, rawY] = point;
        const numericX = Number(rawX);
        if (!Number.isFinite(numericX)) {
          return null;
        }
        if (rawY == null) {
          return [numericX, null] as [number, number | null];
        }
        const numericY = Number(rawY);
        return Number.isFinite(numericY) ? ([numericX, numericY] as [number, number | null]) : null;
      }

      if (point && typeof point === 'object') {
        const numericX = Number((point as { x?: unknown }).x);
        if (!Number.isFinite(numericX)) {
          return null;
        }
        const rawY = (point as { y?: unknown }).y;
        if (rawY == null) {
          return [numericX, null] as [number, number | null];
        }
        const numericY = Number(rawY);
        return Number.isFinite(numericY) ? ([numericX, numericY] as [number, number | null]) : null;
      }

      return null;
    })
    .filter(
      (point): point is [number, number | null] =>
        Array.isArray(point) && Number.isFinite(point[0]) && (point[1] === null || Number.isFinite(point[1])),
    )
    .sort((a, b) => a[0] - b[0]);
};

interface ISeriesObj {
  name: string;
  data: number[][];
  showInNavigator: boolean;
  color?: string;
  title?: string;
  subtitle?: string;
  ytitle?: string;
  ytitle2?: string;
  custom?: {
    description?: string;
  };
}

const Root = styled.div`
  height: 400px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const logChartColors = (source: string, title: string | null | undefined, seriesCollection: ChartSeriesConfig[]) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const series = seriesCollection.map(seriesItem => ({
    name: seriesItem?.name ?? '(unnamed series)',
    color: seriesItem?.color ?? null,
  }));

  if (!series.length) {
    return;
  }
};

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: 1px solid #e6e9eb;
  background-color: var(--background-chart);
  border-radius: 4px;
  padding: 10px;
  .highcharts-button-symbol {
    width: 3.5px !important;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
  .highcharts-legend-item {
    text {
      text-decoration: none !important;
    }
  }
  .highcharts-legend-item-hidden {
    text {
      text-decoration: none !important;
      opacity: 0.5 !important;
    }
    path {
      text-decoration: none !important;
      opacity: 0.5 !important;
    }
  }
  .double-y-axis-chart .highcharts-legend-item.double-y-axis-legend-divider-item,
  .double-y-axis-chart .highcharts-legend-item:has(.double-y-axis-legend-divider) {
    pointer-events: none !important;
    opacity: 1 !important;

    &.highcharts-legend-item-hidden,
    &.highcharts-legend-item-hidden text,
    &.highcharts-legend-item-hidden path {
      opacity: 1 !important;
    }

    .highcharts-point,
    > rect,
    > path {
      display: none !important;
    }

    .double-y-axis-legend-divider {
      display: block !important;
      height: 0 !important;
      border-top: 1px solid #99a3af !important;
      margin: 8px 0 !important;
      box-sizing: border-box !important;
    }
  }
`;

const AlignedLoader = styled(Loader)`
  left: calc(50% - 25px);
  top: calc(50% - 30px);
`;

HighStock.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

let exportMenuIdCounter = 0;

export default function IndicatorDoubleLineChart({
  category,
  prism,
  prismId,
  indicator,
  cellInfo,
  filterValues,
  tooltipCfg,
}: any) {
  const chartRef = useRef<any>(null);
  const [chartReadyTick, setChartReadyTick] = useState(0);
  const savedUserXExtentsRef = useRef<{ min: number; max: number } | null>(null);
  const [xRangeUserCustom, setXRangeUserCustom] = useState(false);
  const applyingRangePresetRef = useRef(false);
  const pendingDurationPresetAnchorRef = useRef<number | undefined>(undefined);

  const [selectedSeriesNames, setSelectedSeriesNames] = useState<string[] | null>(null);
  const doneHideRef = useRef(false);
  const [seriesOptions, setSeriesOptions] = useState<
    Array<{ name: string; color?: string; visible: boolean; description?: string }>
  >([]);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const exportMenuClassName = useMemo(() => {
    exportMenuIdCounter += 1;
    return `highcharts-indicator-menu-${exportMenuIdCounter}`;
  }, []);
  const combinedMenuClassName = `highcharts-indicator-menu ${exportMenuClassName}`;
  const [chartTitle, setChartTitle] = useState('');
  const [chartSubtitle, setChartSubtitle] = useState('');
  const [chartYtitle, setChartYtitle] = useState('');
  const [chartYtitle2, setChartYtitle2] = useState('');
  const [error, setError] = useState<any>(null);
  let exportFileName = `${cellInfo.topic ? `${cellInfo.topic}_` : ''}${
    cellInfo.symbol ? `${cellInfo.symbol}_` : ''
  }${indicator}`;
  Object.keys(filterValues).forEach(function (key) {
    if (key !== 'TimeResolution') exportFileName += `_${filterValues[key]}`;
  });

  const {
    data: apiData,
    error: dataError,
    isLoading,
  } = useQuery(
    ['fetchIndicatorData', indicator, cellInfo],
    async () => {
      const reversed_colors = [...colors].reverse();
      const obj = await fetchIndicatorData(category, prism, prismId, indicator, cellInfo, filterValues);

      const xLabels1 = obj.row_nodes.map((r: any) => r.name);
      const yLabels1 = obj.col_nodes.map((c: any) => c.name);
      const xLabels2 = obj.row_nodes?.map((r: any) => r.name) ?? [];
      const yLabels2 = obj.secondary_col_nodes?.map((c: any) => c.name) ?? [];
      const descriptionByName = buildColumnDescriptionByName(obj.col_nodes, obj.secondary_col_nodes);

      const rootStyles = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
      const colorSchemeSource =
        Array.isArray(obj?.color_scheme) && obj.color_scheme.length ? obj.color_scheme : reversed_colors;
      const primarySeriesCount = yLabels1.length;
      const secondarySeriesCount = yLabels2.length;
      const totalSeriesCount = primarySeriesCount + secondarySeriesCount;
      const highchartsPalette = (HighStock.getOptions && HighStock.getOptions()?.colors) || [];

      const paletteTokens: string[] = [];
      if (Array.isArray(colorSchemeSource) && colorSchemeSource.length) {
        paletteTokens.push(...colorSchemeSource);
      }
      if (paletteTokens.length < totalSeriesCount) {
        paletteTokens.push(...reversed_colors);
      }

      const normalizePaletteColor = (value: string | undefined, fallbackIndex: number): string | undefined => {
        const rawValue = typeof value === 'string' ? value.trim() : '';
        if (rawValue) {
          const resolved = resolveColorToken(rawValue, rootStyles);
          const normalized = typeof resolved === 'string' ? resolved.trim() : rawValue;
          if (normalized) {
            return normalized;
          }
        }

        const fallback = highchartsPalette[fallbackIndex % (highchartsPalette.length || 1)];
        if (typeof fallback === 'string') {
          const trimmedFallback = fallback.trim();
          if (trimmedFallback) {
            return trimmedFallback;
          }
        }

        const themeToken = reversed_colors[fallbackIndex % reversed_colors.length];
        if (typeof themeToken === 'string') {
          const trimmedToken = themeToken.trim();
          if (trimmedToken) {
            const resolvedToken = resolveColorToken(trimmedToken, rootStyles);
            const normalizedToken = typeof resolvedToken === 'string' ? resolvedToken.trim() : trimmedToken;
            if (normalizedToken) {
              return normalizedToken;
            }
          }
        }

        return undefined;
      };

      const resolvedColorScheme = Array.from({ length: totalSeriesCount }, (_, index) =>
        normalizePaletteColor(paletteTokens[index], index),
      );

      const primaryAxisColors = Array.from(
        { length: primarySeriesCount },
        (_, index) =>
          normalizePaletteColor(resolvedColorScheme[index], index) ?? normalizePaletteColor(undefined, index),
      );
      const secondaryAxisColors = Array.from(
        { length: secondarySeriesCount },
        (_, index) =>
          normalizePaletteColor(resolvedColorScheme[primarySeriesCount + index], primarySeriesCount + index) ??
          normalizePaletteColor(undefined, primarySeriesCount + index),
      );

      setChartTitle(obj.title || '');
      setChartSubtitle(obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''} ${indicator}`);
      setChartYtitle(obj.ytitle || '');
      setChartYtitle2(obj.ytitle2 || obj.ytitle || '');
      const highchartsConfig = obj?.highcharts_config ?? obj?.hicharts_config;
      const rawRangeMode =
        highchartsConfig?.yAxis?.range_mode ??
        highchartsConfig?.yAxis?.rangeMode ??
        highchartsConfig?.yAxis_range_mode ??
        highchartsConfig?.yAxisRangeMode ??
        highchartsConfig?.range_mode ??
        highchartsConfig?.rangeMode ??
        highchartsConfig?.yAxis?.mode ??
        obj?.yAxis?.range_mode ??
        obj?.yAxis?.rangeMode ??
        obj?.yAxis_range_mode ??
        obj?.yAxisRangeMode ??
        obj?.range_mode ??
        obj?.rangeMode ??
        obj?.yAxis?.mode;
      const normalizedRangeMode = typeof rawRangeMode === 'string' ? rawRangeMode.trim().toLowerCase() : '';
      const rawPaddingRatio =
        highchartsConfig?.yAxis?.padding_ratio ??
        highchartsConfig?.yAxis?.paddingRatio ??
        highchartsConfig?.yAxis_padding_ratio ??
        highchartsConfig?.yAxisPaddingRatio ??
        highchartsConfig?.padding_ratio ??
        highchartsConfig?.paddingRatio ??
        obj?.yAxis?.padding_ratio ??
        obj?.yAxis?.paddingRatio ??
        obj?.yAxis_padding_ratio ??
        obj?.yAxisPaddingRatio ??
        obj?.padding_ratio ??
        obj?.paddingRatio;
      const parsedPaddingRatio = Number(rawPaddingRatio);
      const normalizedPaddingRatio =
        Number.isFinite(parsedPaddingRatio) && parsedPaddingRatio > 1 && parsedPaddingRatio <= 100
          ? parsedPaddingRatio / 100
          : parsedPaddingRatio;
      const yAxisPaddingRatio =
        Number.isFinite(normalizedPaddingRatio) && normalizedPaddingRatio >= 0 && normalizedPaddingRatio < 1
          ? normalizedPaddingRatio
          : 0.2;
      const isPaddedMode =
        normalizedRangeMode === '' ||
        normalizedRangeMode === 'padded-visible' ||
        normalizedRangeMode === 'padded_visible' ||
        normalizedRangeMode === 'paddedvisible' ||
        normalizedRangeMode === 'dynamic' ||
        normalizedRangeMode === 'auto';
      const yAxisRangeMode = isPaddedMode ? 'padded-visible' : 'zero-based';
      const seriesData1: any[] = [];
      const seriesData2: any[] = [];
      for (let j = 0; j < yLabels1.length; ++j) {
        const colIndex = yLabels1.length - j - 1;
        const columnNode = obj.col_nodes[colIndex];
        const seriesName = yLabels1[colIndex];
        const seriesDescription =
          resolveColumnNodeDescription(columnNode, obj.secondary_col_nodes, descriptionByName) ??
          descriptionByName[seriesName];
        const seriesObj1: ISeriesObj = {
          name: seriesName,
          data: [],
          showInNavigator: true,
          color: primaryAxisColors[primarySeriesCount - j - 1],
          ...(typeof seriesDescription === 'string' && seriesDescription.trim()
            ? { custom: { description: seriesDescription } }
            : {}),
        };
        for (let i = 0; i < xLabels1.length; ++i) {
          const value: number = obj.data[i][yLabels1.length - j - 1] as unknown as number;
          seriesObj1.data.push([moment.utc(xLabels1[i]).valueOf(), value]);
        }
        seriesObj1.title = obj.title || '';
        seriesObj1.subtitle = obj.subtitle || `${cellInfo.topic || ''} ${cellInfo.symbol || ''} ${indicator}`;
        seriesObj1.ytitle = obj.ytitle || '';
        seriesData1.push(seriesObj1);
      }

      for (let j = 0; j < yLabels2.length; ++j) {
        const colIndex = yLabels2.length - j - 1;
        const columnNode = obj.secondary_col_nodes?.[colIndex];
        const seriesName = yLabels2[colIndex];
        const seriesDescription =
          resolveColumnNodeDescription(columnNode, obj.col_nodes, descriptionByName) ?? descriptionByName[seriesName];
        const seriesObj2: ISeriesObj = {
          name: seriesName,
          data: [],
          showInNavigator: true,
          color: secondaryAxisColors[secondarySeriesCount - j - 1],
          ...(typeof seriesDescription === 'string' && seriesDescription.trim()
            ? { custom: { description: seriesDescription } }
            : {}),
        };
        for (let i = 0; i < xLabels2.length; ++i) {
          const value: number = obj.secondary_data[i][yLabels2.length - j - 1] as unknown as number;
          seriesObj2.data.push([moment.utc(xLabels2[i]).valueOf(), value]);
        }
        seriesData2.push(seriesObj2);
      }
      return {
        seriesData1,
        seriesData2,
        seriesDescriptionByName: descriptionByName,
        colorScheme: resolvedColorScheme,
        highchartsConfig,
        sma: obj.sma,
        smaType: obj.sma_type ?? obj.smaType ?? null,
        defaultTimeWindow: obj?.default_time_window,
        yAxisRangeMode,
        yAxisPaddingRatio,
        numberOfLines: obj?.number_of_lines,
        dropdownLegendEnabled: !!obj.dropdownLegend,
      };
    },
    queryOptions,
  );

  const { smaOptions, smaValue, setSmaValue, smaEnabled } = useSmaState(apiData?.sma);

  const normalizedSmaType = useMemo(() => {
    const rawType = apiData?.smaType;
    if (typeof rawType !== 'string') {
      return null;
    }
    const normalized = rawType.trim().toLowerCase();
    if (normalized === 'left' || normalized === 'right') {
      return normalized;
    }
    return null;
  }, [apiData?.smaType]);

  const shouldSmoothLeft = useMemo(() => {
    if (!smaEnabled || smaValue <= 1) {
      return false;
    }
    if (!normalizedSmaType) {
      return true;
    }
    return normalizedSmaType === 'right';
  }, [normalizedSmaType, smaEnabled, smaValue]);

  const shouldSmoothRight = useMemo(() => {
    if (!smaEnabled || smaValue <= 1) {
      return false;
    }
    if (!normalizedSmaType) {
      return true;
    }
    return normalizedSmaType === 'left';
  }, [normalizedSmaType, smaEnabled, smaValue]);

  const defaultRangeIndex = useMemo(
    () => getRangeSelectorIndexFromWindow(apiData?.defaultTimeWindow),
    [apiData?.defaultTimeWindow],
  );

  const [selectedRangeSelector, setSelectedRangeSelector] = useState<number>(
    () => defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX,
  );

  useEffect(() => {
    const nextIndex = defaultRangeIndex ?? DEFAULT_RANGE_SELECTOR_INDEX;
    setSelectedRangeSelector(prev => (prev === nextIndex ? prev : nextIndex));
  }, [defaultRangeIndex, apiData]);

  useEffect(() => {
    setXRangeUserCustom(false);
  }, [defaultRangeIndex]);

  const resolvedSeriesData1 = useMemo(() => {
    if (!apiData?.seriesData1) {
      return undefined;
    }

    return apiData.seriesData1.map(series => {
      const baseData = Array.isArray(series?.data) ? series.data : [];
      if (!shouldSmoothLeft) {
        return { ...series, data: [...baseData] };
      }

      const sortedBaseData = [...baseData].sort((a, b) => a[0] - b[0]);
      const smaPoints = simpleMovingAverage(sortedBaseData, smaValue);
      if (smaPoints.length === 0) {
        return { ...series, data: [...baseData] };
      }

      return {
        ...series,
        data: smaPoints,
      };
    });
  }, [apiData, shouldSmoothLeft, smaValue]);

  const resolvedSeriesData2 = useMemo(() => {
    if (!apiData?.seriesData2) {
      return undefined;
    }

    return apiData.seriesData2.map(series => {
      const baseData = Array.isArray(series?.data) ? series.data : [];
      if (!shouldSmoothRight) {
        return { ...series, data: [...baseData] };
      }

      const sortedBaseData = [...baseData].sort((a, b) => a[0] - b[0]);
      const smaPoints = simpleMovingAverage(sortedBaseData, smaValue);
      if (smaPoints.length === 0) {
        return { ...series, data: [...baseData] };
      }

      return {
        ...series,
        data: smaPoints,
      };
    });
  }, [apiData, shouldSmoothRight, smaValue]);

  const seriesData1 = resolvedSeriesData1 ?? apiData?.seriesData1;
  const seriesData2 = resolvedSeriesData2 ?? apiData?.seriesData2;
  const numberOfLines = apiData?.numberOfLines;
  const dropdownLegendEnabled = Boolean(apiData?.dropdownLegendEnabled);

  const seriesOptionNames = useMemo(() => seriesOptions.map(option => option.name), [seriesOptions]);
  const defaultPartialSelection = useMemo(
    () => seriesOptions.filter(option => option.visible).map(option => option.name),
    [seriesOptions],
  );
  const showSeriesSelector = dropdownLegendEnabled && seriesOptions.length > 1;
  const isSeriesSelectDisabled = !dropdownLegendEnabled || !seriesOptions.length;
  const shouldRenderControls = Boolean(!isLoading && !isExportMenuOpen && (smaEnabled || showSeriesSelector));

  const handleSeriesMetadataChange = useCallback(
    (meta: Array<{ name: string; color?: string; visible: boolean; description?: string }>) => {
      setSeriesOptions(meta);
    },
    [],
  );

  const handleSeriesSelectionChange = useCallback(
    (selection: string[]) => {
      const selectionSet = new Set(selection);
      const orderedSelection = seriesOptionNames.filter(name => selectionSet.has(name));
      if (orderedSelection.length === seriesOptionNames.length) {
        setSelectedSeriesNames(null);
        return;
      }
      setSelectedSeriesNames(orderedSelection);
    },
    [seriesOptionNames],
  );

  useEffect(() => {
    if (!apiData) {
      setIsExportMenuOpen(false);
    }
  }, [apiData]);

  useEffect(() => {
    if (dataError) {
      setError(dataError);
    }
    if (isLoading) {
      setError(null);
    }
  }, [dataError, isLoading]);

  const handleSmaChange = (value: unknown) => {
    setSmaValue(Number(value));
  };

  const displayedSmaValue = useMemo(() => {
    if (!smaOptions.includes(smaValue)) {
      return smaOptions[0] ?? DEFAULT_SMA_VALUE;
    }
    return smaValue;
  }, [smaOptions, smaValue]);

  const rawSeries = useMemo(() => {
    const seriesList: ChartSeriesConfig[] = [];

    (seriesData2 ?? []).forEach((series: ISeriesObj) => {
      if (!series?.name) return;
      seriesList.push({
        type: 'spline',
        name: series.name,
        data: normalizeSeriesData(series.data as PointLike[]),
        yAxis: SECONDARY_AXIS_ID,
        color: series.color,
        showInNavigator: series.showInNavigator !== false,
        ...(series.custom?.description ? { custom: { description: series.custom.description } } : {}),
      });
    });

    (seriesData1 ?? []).forEach((series: ISeriesObj) => {
      if (!series?.name) return;
      seriesList.push({
        type: 'spline',
        name: series.name,
        data: normalizeSeriesData(series.data as PointLike[]),
        yAxis: PRIMARY_AXIS_ID,
        color: series.color,
        showInNavigator: series.showInNavigator !== false,
        ...(series.custom?.description ? { custom: { description: series.custom.description } } : {}),
      });
    });

    return seriesList;
  }, [seriesData1, seriesData2]);

  useEffect(() => {
    logChartColors('IndicatorDoubleLineChart/raw', chartTitle, rawSeries);
  }, [chartTitle, rawSeries]);

  const baseSeriesNames = useMemo(
    () => rawSeries.map(series => series?.name).filter((name): name is string => Boolean(name)),
    [rawSeries],
  );

  const normalizeSelection = useCallback(
    (selection: string[] | null | undefined) => {
      if (selection === null || selection === undefined) {
        return Array.from(
          resolveInitialDoubleYAxisVisibleNames(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG, {
            numberOfLines,
          }),
        );
      }
      const selectionSet = new Set(selection);
      return baseSeriesNames.filter(name => selectionSet.has(name));
    },
    [baseSeriesNames, rawSeries, numberOfLines],
  );

  const areSelectionsEqual = useCallback(
    (a: string[] | null | undefined, b: string[] | null | undefined) => {
      if (a === b) return true;
      const normalizedA = normalizeSelection(a);
      const normalizedB = normalizeSelection(b);
      if (normalizedA.length !== normalizedB.length) {
        return false;
      }
      return normalizedA.every((value, index) => normalizedB[index] === value);
    },
    [normalizeSelection],
  );

  const updateSelectedSeriesFromChart = useCallback(
    (chartInstance?: HighStock.Chart, revealedSeriesName?: string, allowCollapseToEmpty = false) => {
      if (dropdownLegendEnabled) {
        return;
      }

      const chart = chartInstance ?? chartRef.current;
      if (!chart) return;

      syncLineChartSelectedSeriesFromChart({
        chart,
        baseSeriesNames,
        selectedSeriesNames,
        areSelectionsEqual,
        setSelectedSeriesNames,
        revealedSeriesName,
        allowCollapseToEmpty,
        collapseFullSelectionToNull: false,
      });
    },
    [areSelectionsEqual, baseSeriesNames, dropdownLegendEnabled, selectedSeriesNames],
  );

  const resolveVisibleNamesWhenNull = useCallback(
    () =>
      Array.from(
        resolveInitialDoubleYAxisVisibleNames(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG, {
          numberOfLines,
        }),
      ),
    [rawSeries, numberOfLines],
  );

  const { legendEventsItemClick } = useLineChartLegendClickHandlers({
    baseSeriesNames,
    selectedSeriesNames,
    setSelectedSeriesNames,
    resolveVisibleNamesWhenNull,
    collapseFullSelectionToNull: false,
  });

  const layoutSeriesCount = useMemo(() => baseSeriesNames.length, [baseSeriesNames.length]);

  const visibleSeriesNames = useMemo(
    () =>
      resolveInitialDoubleYAxisVisibleNames(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG, {
        numberOfLines,
        selectedSeriesNames,
      }),
    [rawSeries, numberOfLines, selectedSeriesNames],
  );

  const visibleSeriesNameList = useMemo(() => Array.from(visibleSeriesNames), [visibleSeriesNames]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !baseSeriesNames.length) {
      return;
    }

    const applySelection = () => {
      applyLineChartSelectionToChart(chart, baseSeriesNames, selectedSeriesNames, {
        explicitVisibleNames: visibleSeriesNameList,
      });
    };

    applySelection();
    const rafId = requestAnimationFrame(applySelection);
    return () => cancelAnimationFrame(rafId);
  }, [baseSeriesNames, selectedSeriesNames, visibleSeriesNameList, chartReadyTick]);

  useEffect(() => {
    doneHideRef.current = false;
  }, [seriesData1, seriesData2, numberOfLines]);

  useEffect(() => {
    const legendOptions = buildDoubleYAxisLegendOptions(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG);
    handleSeriesMetadataChange(
      legendOptions.map(option => ({
        ...option,
        description: option.description ?? apiData?.seriesDescriptionByName?.[option.name],
        visible: option.name ? visibleSeriesNames.has(option.name) : false,
      })),
    );
  }, [rawSeries, visibleSeriesNames, handleSeriesMetadataChange, apiData?.seriesDescriptionByName]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    updateSelectedSeriesFromChart(chart);
    // Only re-sync when the underlying series set changes, not on every selection update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSeries]);

  const preparedSeries = useMemo((): ChartSeriesConfig[] => {
    return rawSeries.map(series => {
      if (!series || !series.name) {
        return { ...series, compare: undefined, linkedTo: undefined } as ChartSeriesConfig;
      }

      const isVisible = visibleSeriesNames.has(series.name);

      return {
        ...series,
        visible: isVisible,
        showInLegend: true,
        showInNavigator: series.showInNavigator !== false,
        compare: undefined,
        linkedTo: undefined,
      } as ChartSeriesConfig;
    });
  }, [rawSeries, visibleSeriesNames]);

  useEffect(() => {
    logChartColors('IndicatorDoubleLineChart/prepared', chartTitle, preparedSeries);
  }, [chartTitle, preparedSeries]);

  const chartSeriesOptions = useMemo(
    () =>
      appendDoubleYAxisChartSeries(
        preparedSeries,
        DOUBLE_Y_AXIS_LEGEND_CONFIG,
        series => {
          const plottedData = normalizeSeriesData(series.data as PointLike[] | undefined);
          return {
            type: 'line',
            name: series.name,
            data: plottedData,
            yAxis: series.yAxis ?? PRIMARY_AXIS_ID,
            color: typeof series.color === 'string' ? series.color : undefined,
            visible: series.visible !== false,
            animation: false,
            lineWidth: 2,
            marker: { enabled: false },
            states: {
              inactive: { opacity: 1 },
              hover: { lineWidthPlus: 0 },
            },
            threshold: undefined,
            softThreshold: false,
            compare: undefined,
            linkedTo: undefined,
            dataGrouping: { enabled: false },
            boostThreshold: 0,
            turboThreshold: 0,
            cropThreshold: plottedData.length + 1,
            ...(series.custom?.description ? { custom: { description: series.custom.description } } : {}),
          };
        },
        { includeInlineLegendDivider: !dropdownLegendEnabled },
      ),
    [dropdownLegendEnabled, preparedSeries],
  );

  const showInlineLegendDivider = useMemo(() => {
    if (dropdownLegendEnabled) return false;
    const leftCount = preparedSeries.filter(series => (series.yAxis ?? PRIMARY_AXIS_ID) === SECONDARY_AXIS_ID).length;
    const rightCount = preparedSeries.filter(series => series.yAxis === PRIMARY_AXIS_ID).length;
    return leftCount > 0 && rightCount > 0;
  }, [dropdownLegendEnabled, preparedSeries]);

  useEffect(() => {
    if (!chartSeriesOptions.length) {
      return;
    }
    logChartColors('IndicatorDoubleLineChart/options', chartTitle, chartSeriesOptions as ChartSeriesConfig[]);
  }, [chartSeriesOptions, chartTitle]);

  useEffect(() => {
    if (!baseSeriesNames.length) {
      if (selectedSeriesNames !== null) {
        setSelectedSeriesNames(null);
      }
      return;
    }

    if (selectedSeriesNames === null) {
      return;
    }

    if (!Array.isArray(selectedSeriesNames)) {
      setSelectedSeriesNames(null);
      return;
    }

    if (selectedSeriesNames.length === 0) {
      return;
    }

    const baseNameSet = new Set(baseSeriesNames);
    const filtered = selectedSeriesNames.filter(name => baseNameSet.has(name));

    if (!filtered.length) {
      setSelectedSeriesNames(null);
      return;
    }

    if (filtered.length === selectedSeriesNames.length) {
      return;
    }

    setSelectedSeriesNames(filtered);
  }, [baseSeriesNames, selectedSeriesNames]);

  const valuesBg = apiData?.highchartsConfig?.xAxis_custom_background_values;
  const colorsBg = apiData?.highchartsConfig?.xAxis_custom_background_colors;
  const labelsBg = apiData?.highchartsConfig?.xAxis_custom_background_labels;

  const plotBands = useMemo(() => {
    const bands =
      colorsBg &&
      colorsBg.map((color: any, i: number) => ({
        from: moment.utc(valuesBg[i]).valueOf(),
        to: moment.utc(valuesBg[i + 1]).valueOf(),
        color: hex2rgba(color, plotBandOpacity),
        label: {
          text: labelsBg?.[i],
          rotation: 90,
          align: 'left',
          useHtml: true,
          style: {
            color: '#000000',
          },
        },
      }));
    return bands;
  }, []);

  const chartOptions = useMemo(() => {
    const legendY = layoutSeriesCount < 3 ? -3 : -14;
    const {
      marginRight,
      legendX,
      verticalInlineLegend,
      rightAxisMargin,
      rightAxisOffset,
      rightAxisLabelAlign,
      rightAxisLabelX,
      spacingRight,
      legendMargin,
    } = resolveDoubleYAxisChartLayout({
      dropdownLegendEnabled,
      layoutSeriesCount,
      defaultMarginRight: 76,
      leftAxisMargin: 30,
      leftAxisOffset: 0,
    });
    const options = {
      custom: {
        seriesDescriptionByName: apiData?.seriesDescriptionByName ?? {},
      },
      chart: {
        className: 'double-y-axis-chart',
        type: 'spline',
        marginLeft: 76,
        marginRight,
        spacingRight: spacingRight ?? 16,
        marginTop: chartSubtitle ? 100 : 80,
        animation: false,
        custom: {
          seriesDescriptionByName: apiData?.seriesDescriptionByName ?? {},
        },
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
        },
        events: {
          load: function (this: HighStock.Chart) {
            if (showInlineLegendDivider) {
              maintainDoubleYAxisInlineLegendDivider(this);
            }
            syncLineChartLegendTooltips(this, tooltipCfg);
            if (!dropdownLegendEnabled) {
              scheduleLineChartSelectedSeriesSync(this, updateSelectedSeriesFromChart);
            }
          },
          redraw: function (this: HighStock.Chart) {
            if (showInlineLegendDivider) {
              maintainDoubleYAxisInlineLegendDivider(this);
            }
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
          render: function (this: HighStock.Chart) {
            if (showInlineLegendDivider) {
              maintainDoubleYAxisInlineLegendDivider(this);
            }
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
        },
      },
      boost: { enabled: false },
      xAxis: {
        type: 'datetime',
        ordinal: false,
        startOnTick: false,
        endOnTick: false,
        plotBands: plotBands,
      },
      rangeSelector: {
        buttonSpacing: 12,
        inputEnabled: false,
        selected: xRangeUserCustom ? null : (selectedRangeSelector ?? DEFAULT_RANGE_SELECTOR_INDEX),
        floating: true,
        labelStyle: { display: 'none' },
        x: 0,
        y: chartSubtitle ? 0 : 16,
        buttonTheme: {
          r: 15,
          fill: 'transparent',
          height: 26,
          style: {
            fill: 'transparent',
            fontSize: 13,
            marginRight: 15,
            color: '#33485E',
            background: 'transparent',
            fontWeight: 500,
          },
          states: {
            hover: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 500,
              },
            },
            select: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 600,
              },
            },
          },
        },
        buttonPosition: {
          x: -48,
          y: -86,
        },
        buttons: [
          {
            type: 'month',
            count: 1,
            text: '1M',
            title: 'View 1 month',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(0);
              },
            },
          },
          {
            type: 'month',
            count: 3,
            text: '3M',
            title: 'View 3 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(1);
              },
            },
          },
          {
            type: 'month',
            count: 6,
            text: '6M',
            title: 'View 6 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(2);
              },
            },
          },
          {
            type: 'ytd',
            text: 'YTD',
            title: 'View year to date',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector(3);
              },
            },
          },
          {
            type: 'year',
            count: 1,
            text: '1Y',
            title: 'View 1 year',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector(4);
              },
            },
          },
          {
            type: 'all',
            text: 'Max',
            title: 'View all',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector(MAX_RANGE_SELECTOR_INDEX);
              },
            },
          },
        ],
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof HighStock.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        buttons: {
          contextButton: {
            x: 0,
            y: 0,
            symbol: 'ellipsisvertical',
            menuClassName: combinedMenuClassName,
            menuItems: getLineChartExportMenuItems(baseSeriesNames.length),
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 20,
        y: 24,
        style: {
          fontSize: '18px',
          color: 'var(--color-gray-6)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: '',
        align: 'left',
        x: 20,
        y: 44,
        style: {
          fontSize: '14px',
          color: 'var(--color-gray-6)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: !dropdownLegendEnabled && layoutSeriesCount > 1 && !isLoading,
        floating: layoutSeriesCount < 3,
        layout: layoutSeriesCount < 3 ? 'horizontal' : 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: legendX ?? (layoutSeriesCount < 3 ? -30 : -10),
        y: legendY,
        maxHeight: 250,
        reversed: false,
        margin: legendMargin ?? (!dropdownLegendEnabled ? 8 : undefined),
        useHTML: showInlineLegendDivider,
        labelFormatter: showInlineLegendDivider ? doubleYAxisLegendLabelFormatter : undefined,
        itemHiddenStyle: { color: '#666666', textDecoration: 'none' },
        events: {
          itemClick: legendEventsItemClick,
        },
      },
      yAxis: [
        {
          id: PRIMARY_AXIS_ID,
          linkedTo: undefined,
          tickPixelInterval: 90,
          showFirstLabel: true,
          showLastLabel: true,
          title: {
            text: chartYtitle,
            color: 'var(--color-gray-6)',
            x: 19,
          },
          opposite: true,
          offset: verticalInlineLegend ? (rightAxisOffset ?? 0) : 30,
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'silver',
            },
          ],
          labels: {
            format: '{value}',
            style: {
              color: 'var(--color-gray-6)',
            },
            align: rightAxisLabelAlign ?? 'right',
            x: rightAxisLabelX ?? 18,
            y: -6,
          },
          margin: rightAxisMargin ?? 30,
          ...(apiData?.yAxisRangeMode === 'padded-visible'
            ? {
                startOnTick: false,
                endOnTick: false,
              }
            : {}),
        },
        {
          id: SECONDARY_AXIS_ID,
          linkedTo: undefined,
          tickPixelInterval: 90,
          showFirstLabel: true,
          showLastLabel: true,
          title: {
            text: chartYtitle2,
            color: 'var(--color-gray-6)',
            x: -21,
          },
          opposite: false,
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'silver',
            },
          ],
          labels: {
            format: '{value}',
            style: {
              color: 'var(--color-gray-6)',
            },
            align: 'left',
            x: -46,
            y: -6,
          },
          margin: 30,
          ...(apiData?.yAxisRangeMode === 'padded-visible'
            ? {
                startOnTick: false,
                endOnTick: false,
              }
            : {}),
        },
      ],
      tooltip: {
        ...lineChartHighchartsTooltipCfg(tooltipCfg),
        useHTML: true,
        shape: 'rect',
        backgroundColor: '#fff',
        borderRadius: 5,
        borderColor: 'transparent',
        shadow: true,
      },
      lang: {
        noData: '',
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: 'var(--color-chart-no-data)',
        },
      },
      series: chartSeriesOptions,
      plotOptions: {
        series: {
          selected: false,
          compare: undefined,
          dataGrouping: { enabled: false },
          turboThreshold: 0,
          marker: { enabled: false },
          linkedTo: undefined,
          showInNavigator: true,
          animation: false,
          line: {
            selected: false,
          },
          events: {
            afterAnimate: function (event: HighStock.SeriesAfterAnimateCallbackFunction) {
              const chart = (event as { target?: { chart?: HighStock.Chart } }).target?.chart;
              if (!chart || doneHideRef.current) {
                return;
              }

              const exportableSeries =
                chart.series?.filter(
                  series => series.name && !/^Navigator \d+$/.test(series.name) && !isLegendAxisDivider(series.name),
                ) ?? [];
              if (!exportableSeries.length) {
                return;
              }

              applyLineChartSelectionToChart(chart, baseSeriesNames, selectedSeriesNames, {
                explicitVisibleNames: Array.from(visibleSeriesNames),
              });
              doneHideRef.current = true;
              updateSelectedSeriesFromChart(chart);
            },
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              yAxis: [
                {
                  labels: {
                    align: 'right',
                    x: 20,
                    y: -6,
                  },
                  showLastLabel: false,
                },
                {
                  labels: {
                    align: 'right',
                    x: 20,
                    y: -6,
                  },
                  showLastLabel: false,
                },
                {
                  visible: false,
                },
              ],
            },
          },
        ],
      },
    };

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartSeriesOptions,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
    chartYtitle2,
    exportFileName,
    plotBands,
    isLoading,
    preparedSeries,
    selectedSeriesNames,
    selectedRangeSelector,
    xRangeUserCustom,
    updateSelectedSeriesFromChart,
    legendEventsItemClick,
    visibleSeriesNames,
    baseSeriesNames,
    apiData?.yAxisRangeMode,
    baseSeriesNames.length,
    combinedMenuClassName,
    dropdownLegendEnabled,
    layoutSeriesCount,
    numberOfLines,
    showInlineLegendDivider,
  ]);

  const lastAppliedRangeRef = useRef<unknown>(null);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.redraw();

    if (lastAppliedRangeRef.current !== selectedRangeSelector) {
      setXRangeUserCustom(false);
      applyingRangePresetRef.current = true;
      const anchorFromButton = pendingDurationPresetAnchorRef.current;
      pendingDurationPresetAnchorRef.current = undefined;
      const applied = applyRangeSelectorToChart(chart, selectedRangeSelector, anchorFromButton);
      if (applied) {
        lastAppliedRangeRef.current = selectedRangeSelector;
        savedUserXExtentsRef.current = null;
      }
      requestAnimationFrame(() => {
        applyingRangePresetRef.current = false;
      });
    } else {
      const xAxis = chart.xAxis?.[0];
      const saved = savedUserXExtentsRef.current;
      if (saved && xAxis) {
        requestAnimationFrame(() => {
          const cur = readFiniteXExtentsFromAxis(xAxis);
          if (cur && xExtentsDiffer(cur, saved)) {
            xAxis.setExtremes(saved.min, saved.max, false, false);
          }
        });
      }
    }
  }, [selectedRangeSelector, chartRef, preparedSeries]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || xRangeUserCustom) return;

    const xValues = extractSeriesXValues(chart.series || []);
    if (!xValues.length) return;

    const xAxis = chart.xAxis?.[0];
    if (!xAxis || typeof xAxis.getExtremes !== 'function') return;

    const { min, max } = xAxis.getExtremes();
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    if (areChartXExtremesCanonicalForRangeIndex(chart, selectedRangeSelector, min, max)) return;

    applyingRangePresetRef.current = true;
    applyRangeSelectorToChart(chart, selectedRangeSelector);
    requestAnimationFrame(() => {
      applyingRangePresetRef.current = false;
    });
  }, [preparedSeries, selectedRangeSelector, xRangeUserCustom]);

  const updateYAxesForVisibleWindow = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const xAxis = chart.xAxis?.[0];
    const primaryAxis = chart.yAxis?.[0];
    const secondaryAxis = chart.yAxis?.[1];
    if (!xAxis || !primaryAxis || !secondaryAxis) return;

    if (apiData?.yAxisRangeMode !== 'padded-visible') {
      primaryAxis.setExtremes(undefined, undefined, false, false);
      secondaryAxis.setExtremes(undefined, undefined, false, false);
      chart.redraw();
      return;
    }

    const xExtremes = xAxis.getExtremes();
    const minX = xExtremes?.min;
    const maxX = xExtremes?.max;
    const paddingRatio =
      Number.isFinite(apiData?.yAxisPaddingRatio) && apiData.yAxisPaddingRatio >= 0
        ? Number(apiData.yAxisPaddingRatio)
        : 0.2;

    const collectWindowValues = (axis: HighStock.Axis) =>
      (Array.isArray(axis.series) ? axis.series : [])
        .filter(
          (series: any) =>
            series &&
            series.name &&
            !/^Navigator \d+$/.test(series.name) &&
            !isLegendAxisDivider(series.name) &&
            series.visible !== false,
        )
        .flatMap((series: any) => {
          const seriesData = Array.isArray(series.options?.data) ? (series.options.data as PointLike[]) : [];
          const normalizedSeriesData: Array<[number, number | null]> = normalizeSeriesData(seriesData);
          return normalizedSeriesData
            .filter(
              ([x]: [number, number | null]) =>
                !Number.isFinite(minX) || !Number.isFinite(maxX) || (x >= minX && x <= maxX),
            )
            .map(([, y]: [number, number | null]) => y)
            .filter((value: number | null): value is number => Number.isFinite(value));
        });

    const primaryLimits = deriveAxisLimits(collectWindowValues(primaryAxis), paddingRatio);
    const secondaryLimits = deriveAxisLimits(collectWindowValues(secondaryAxis), paddingRatio);

    if (typeof primaryLimits.min === 'number' && typeof primaryLimits.max === 'number') {
      primaryAxis.setExtremes(primaryLimits.min, primaryLimits.max, false, false);
    } else {
      primaryAxis.setExtremes(undefined, undefined, false, false);
    }
    if (typeof secondaryLimits.min === 'number' && typeof secondaryLimits.max === 'number') {
      secondaryAxis.setExtremes(secondaryLimits.min, secondaryLimits.max, false, false);
    } else {
      secondaryAxis.setExtremes(undefined, undefined, false, false);
    }
    chart.redraw();
  }, [apiData?.yAxisPaddingRatio, apiData?.yAxisRangeMode]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateYAxesForVisibleWindow();
    });

    return () => cancelAnimationFrame(rafId);
  }, [preparedSeries, selectedRangeSelector, updateYAxesForVisibleWindow]);

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let disposed = false;

    const setup = () => {
      if (disposed) return;
      const chart = chartRef.current;
      if (!chart) {
        requestAnimationFrame(setup);
        return;
      }

      const doc = chart.renderTo?.ownerDocument;
      if (!doc) return;

      const body = doc.body;
      if (!body) return;

      let lastVisible: boolean | null = null;
      const menuSelector = `.${exportMenuClassName}`;
      const computeVisibility = () => {
        const menu = body.querySelector(menuSelector) as HTMLElement | null;
        if (!menu) {
          return false;
        }
        const view = doc.defaultView;
        const style = view ? view.getComputedStyle(menu) : null;
        if (style) {
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
            return false;
          }
        } else if (menu.style.display === 'none') {
          return false;
        }

        return menu.childElementCount > 0;
      };

      const emitIfChanged = () => {
        const visible = computeVisibility();
        if (visible !== lastVisible) {
          lastVisible = visible;
          setIsExportMenuOpen(visible);
        }
      };

      emitIfChanged();

      const observer = new MutationObserver(() => {
        emitIfChanged();
      });

      observer.observe(body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });

      const handleInteraction = () => {
        requestAnimationFrame(emitIfChanged);
      };

      body.addEventListener('click', handleInteraction, true);
      body.addEventListener('keydown', handleInteraction, true);

      cleanup = () => {
        observer.disconnect();
        body.removeEventListener('click', handleInteraction, true);
        body.removeEventListener('keydown', handleInteraction, true);
        setIsExportMenuOpen(false);
      };
    };

    setup();

    return () => {
      disposed = true;
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    };
  }, [exportMenuClassName]);

  useEffect(() => {
    const chart = chartRef.current;
    const xAxis = chart?.xAxis?.[0];
    if (!chart || !xAxis) return;

    const handleAfterSetExtremes = (e: any) => {
      const picked = pickXExtentsFromAfterSetExtremesEvent(e);
      if (picked && shouldPersistUserXExtentsFromEvent(e, { applyingRangePreset: applyingRangePresetRef.current })) {
        savedUserXExtentsRef.current = picked;
      }
      const chartInstance = chartRef.current;
      const evMin = e?.min;
      const evMax = e?.max;
      if (
        !applyingRangePresetRef.current &&
        chartInstance &&
        Number.isFinite(evMin) &&
        Number.isFinite(evMax) &&
        !areChartXExtremesCanonicalForRangeIndex(chartInstance, selectedRangeSelector, evMin, evMax)
      ) {
        setXRangeUserCustom(true);
      }
      requestAnimationFrame(() => {
        updateYAxesForVisibleWindow();
      });
    };

    HighStock.addEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    return () => {
      HighStock.removeEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    };
  }, [preparedSeries, selectedRangeSelector, updateYAxesForVisibleWindow]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    chart.yAxis.forEach((axis: HighStock.Axis) => {
      axis.update(
        {
          labels: {
            style: {
              color: 'var(--color-gray-6)',
            },
          },
        },
        false,
      );
    });

    chart.redraw();
  }, [chartOptions]);

  useEffect(() => {
    if (error?.message) {
      // chartRef.current?.showLoading(error?.message);
    } else if (!preparedSeries.length || preparedSeries.every(series => !series.data?.length)) {
      // chartRef.current?.showLoading('No Data');
    } else {
      chartRef.current?.hideLoading();
    }
  });

  return (
    <Root>
      {shouldRenderControls && (
        <LegendControlsWrapper>
          {smaEnabled && (
            <IndicatorSmaControls
              className="sma-row"
              options={smaOptions}
              value={displayedSmaValue}
              onChange={handleSmaChange}
            />
          )}
          {showSeriesSelector && (
            <SeriesControls
              invertMenuOrder={false}
              seriesOptions={seriesOptions}
              selectedSeries={selectedSeriesNames}
              defaultPartialSelection={defaultPartialSelection}
              onSeriesChange={handleSeriesSelectionChange}
              seriesDisabled={isSeriesSelectDisabled || isLoading}
              seriesLabel="Series:"
              menuWidth={220}
              seriesTooltipCfg={tooltipCfg}
            />
          )}
        </LegendControlsWrapper>
      )}
      <HighchartContainer>
        {!isLoading && (
          <HighchartsReact
            containerProps={{ style: { height: '100%' } }}
            style={{ flex: 1 }}
            highcharts={HighStock}
            constructorType={'stockChart'}
            options={chartOptions ?? EMPTY_STOCK_OPTIONS}
            callback={(chart: any) => {
              chartRef.current = chart;
              setChartReadyTick(tick => tick + 1);
            }}
          />
        )}
        {isLoading && <AlignedLoader />}
        {error && <AlertBar message={error.message} />}
      </HighchartContainer>
    </Root>
  );
}
